import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { UserCircle, LogOut, Settings, LayoutGrid, ChevronLeft, ChevronRight } from "lucide-react";
import { logoutFromThisApp } from "../../auth/logout";
import { buildCloudflarePathMaps } from "../../utils/embeddedAppPaths";
import { HARDCODED_NAV_SECTIONS } from "./hardcodedNavSections";
import { buildCloudflareNavSections, buildCloudflareTabGroups, normTabKey } from "./cloudflareNavSections";
import { mergeNavSectionsWithOverride } from "./mergeNavSections";
import { useMergedCloudflareApplications } from "../../hooks/useMergedCloudflareApplications";
import { SidebarCollapsibleSection } from "./SidebarCollapsibleSection";
import type { AuthMeUser } from "../../hooks/useAuthMe";
import type { HardcodedNavSection, ResolvedNavSection } from "./types";
import { filterNavSections } from "@variphigen/shared-dashboards";
import { useModuleConfigContext } from "../../contexts/ModuleConfigContext";

function defaultOpenState(): Record<string, boolean> {
  return {
    dashboard: true,
    cmm: true,
    nvr: true,
    agent: true,
  };
}

function navLinkClassName(isActive: boolean): string {
  return [
    "flex items-center gap-2 rounded-lg px-3 py-2 transition-colors",
    isActive
      ? "bg-blue-500/20 text-blue-700 dark:text-blue-200"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-[#1C202F]/55 dark:hover:text-white",
  ].join(" ");
}

export type BizeyeSidebarProps = {
  authUser: AuthMeUser | null | undefined;
};

export const BizeyeSidebar: React.FC<BizeyeSidebarProps> = ({ authUser }) => {
  const location = useLocation();
  const { modules } = useModuleConfigContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>(defaultOpenState);
  const menuRef = useRef<HTMLDivElement>(null);
  const prevPathRef = useRef<string>(location.pathname);

  const cloudflareApplications = useMergedCloudflareApplications();

  const { byId: embedPathByAppId } = React.useMemo(
    () => buildCloudflarePathMaps(cloudflareApplications),
    [cloudflareApplications]
  );

  const cloudflareTabGroups = React.useMemo(() => buildCloudflareTabGroups(cloudflareApplications), [cloudflareApplications]);

  const cloudflareSections = React.useMemo(
    () => buildCloudflareNavSections(cloudflareTabGroups, embedPathByAppId),
    [cloudflareTabGroups, embedPathByAppId]
  );

  const filteredHardcodedSections = React.useMemo(
    () =>
      filterNavSections(
        HARDCODED_NAV_SECTIONS as unknown as Parameters<typeof filterNavSections>[0],
      ) as HardcodedNavSection[],
    [modules],
  );

  const mergedSections = React.useMemo(
    () => mergeNavSectionsWithOverride(filteredHardcodedSections, cloudflareSections),
    [filteredHardcodedSections, cloudflareSections]
  );

  useEffect(() => {
    setSectionOpen((prev) => {
      const next = { ...prev };
      for (const s of mergedSections) {
        if (next[s.id] === undefined) {
          next[s.id] = true;
        }
      }
      return next;
    });
  }, [mergedSections]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (sidebarCollapsed) {
      setMenuOpen(false);
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;

    const navigatedToCmm = location.pathname.startsWith("/cmm") && !prev.startsWith("/cmm");
    if (navigatedToCmm) {
      setSectionOpen((p) => ({ ...p, cmm: true }));
    }

    if (location.pathname === "/camera-status") {
      setSectionOpen((p) => ({ ...p, dashboard: true }));
    }

    const navigatedToAiAnalytics =
      location.pathname.startsWith("/ai-analytics") &&
      location.pathname !== "/camera-status" &&
      !prev.startsWith("/ai-analytics");
    if (navigatedToAiAnalytics) {
      setSectionOpen((p) => ({ ...p, "ai-analytics": true }));
    }

    const parts = location.pathname.split("/").filter(Boolean);
    const seg = parts[0] === "embed" && parts[1] ? parts[1] : parts[0];
    if (seg) {
      for (const app of cloudflareApplications) {
        if (embedPathByAppId.get(app.id) === seg && normTabKey(app.tab) === "agent") {
          setSectionOpen((p) => ({ ...p, agent: true }));
          break;
        }
      }
    }
  }, [location.pathname, cloudflareApplications, embedPathByAppId]);

  const fullName =
    authUser && (authUser.full_name ?? authUser.name ?? authUser.email)
      ? String(authUser.full_name ?? authUser.name ?? authUser.email)
      : "";

  const normalizedRoles = Array.isArray((authUser as { roles?: unknown })?.roles)
    ? (authUser as { roles: unknown[] }).roles.map((r: unknown) => String(r).toUpperCase())
    : [];
  const explicitRole = String(
    (authUser as { role?: unknown })?.role ??
      (authUser as { userType?: unknown })?.userType ??
      (authUser as { user_type?: unknown })?.user_type ??
      ""
  ).toUpperCase();
  const isViewerUser = normalizedRoles.includes("VIEWER") || explicitRole === "VIEWER";

  const handleLogout = async () => {
    setMenuOpen(false);
    await logoutFromThisApp();
  };

  const handleSectionHeaderClick = (sectionId: string) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      setSectionOpen((prev) => ({ ...prev, [sectionId]: true }));
    } else {
      setSectionOpen((prev) => ({ ...prev, [sectionId]: !(prev[sectionId] ?? true) }));
    }
  };

  const renderMergedSection = (section: ResolvedNavSection, index: number) => {
    const isOpen = sectionOpen[section.id] ?? true;
    const isTabActive = section.isSectionActive(location.pathname);

    return (
      <SidebarCollapsibleSection
        key={section.id}
        title={section.title}
        collapsedTitle={section.collapsedTitle ?? section.title}
        icon={section.icon}
        isTabActive={isTabActive}
        isOpen={isOpen}
        isFirst={index === 0}
        sidebarCollapsed={sidebarCollapsed}
        onHeaderClick={() => handleSectionHeaderClick(section.id)}
        childrenWhenExpanded={
          <>
            {section.hardcodedChildren.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                end={child.end}
                className={({ isActive }) => navLinkClassName(isActive)}
              >
                <child.icon className="h-4 w-4 flex-shrink-0" />
                <span>{child.label}</span>
              </NavLink>
            ))}
            {section.cloudflareChildren.map((item) => (
              <NavLink
                key={item.appId}
                to={item.to}
                state={{ externalUrl: item.externalUrl, tabName: item.tabName, subTab: item.subTab }}
                className={({ isActive }) => navLinkClassName(isActive)}
              >
                <LayoutGrid className="h-4 w-4 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        }
      />
    );
  };

  return (
    <aside
      className={`flex flex-shrink-0 flex-col border-r border-slate-200 bg-white transition-all duration-300 dark:border-slate-800 dark:bg-[#0B0F19] ${
        sidebarCollapsed ? "w-16" : "w-72"
      }`}
    >
      <nav className={`text-sm flex-1 overflow-y-auto pt-4 ${sidebarCollapsed ? "p-2" : "p-4"}`}>
        {mergedSections.map((s, i) => renderMergedSection(s, i))}
      </nav>

      <div className={`relative border-t border-slate-200 dark:border-slate-800 ${sidebarCollapsed ? "p-2" : "p-4"}`} ref={menuRef}>
        {menuOpen && (
          <div
            className={`absolute bottom-full mb-2 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700/80 dark:bg-[#1C202F]/95 ${
              sidebarCollapsed ? "left-2 right-2" : "left-4 right-4"
            }`}
          >
            {!isViewerUser && (
              <NavLink
                to="/member"
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-3 text-sm transition-colors ${
                    isActive
                      ? "bg-blue-500/20 text-blue-700 dark:text-blue-300"
                      : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-[#1C202F]/55"
                  }`
                }
              >
                <UserCircle className="h-4 w-4" />
                <span>Member</span>
              </NavLink>
            )}
            <NavLink
              to="/settings"
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-3 text-sm transition-colors ${
                  isActive
                    ? "bg-blue-500/20 text-blue-700 dark:text-blue-300"
                    : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-[#1C202F]/55"
                }`
              }
            >
              <Settings className="h-4 w-4" />
              <span>Settings</span>
            </NavLink>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 border-t border-slate-200 px-4 py-3 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700/70 dark:text-slate-200 dark:hover:bg-[#1C202F]/55"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        )}
        <div className={`flex gap-2 ${sidebarCollapsed ? "flex-col" : "flex-row"}`}>
          <button
            type="button"
            onClick={() => {
              if (sidebarCollapsed) {
                setSidebarCollapsed(false);
                setMenuOpen(true);
              } else {
                setMenuOpen((o) => !o);
              }
            }}
            title={sidebarCollapsed ? "Account" : undefined}
            className={`flex items-center justify-start rounded-lg transition-colors ${
              sidebarCollapsed ? "p-2.5 w-full" : "p-2.5 flex-1 w-8"
            } ${
              menuOpen
                ? "bg-blue-500/20 text-blue-700 dark:text-blue-200"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-[#1C202F]/55 dark:hover:text-white"
            }`}
            aria-label="Account menu"
          >
            <UserCircle className="h-6 w-6 flex-shrink-0" />
            {!sidebarCollapsed && (
              <div className="ml-2 flex min-w-0 flex-col">
                <span className="truncate text-xs font-medium text-slate-900 dark:text-slate-100">
                  {fullName || "Account"}
                </span>
                {authUser?.email && (
                  <span className="truncate text-[11px] text-slate-500 dark:text-slate-400">{authUser.email}</span>
                )}
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={() => setSidebarCollapsed((c) => !c)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="flex items-center justify-center rounded-lg p-2.5 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-[#1C202F]/60 dark:hover:text-white"
          >
            {sidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </aside>
  );
};
